<template>
	<div :class="['tabs', ...variantClassList]">
		<div
			class="tabs__btns"
			v-if="
				$slots.buttons &&
					(variant === 'verticalBg0' ||
						variant === 'verticalBg1' ||
						variant === 'verticalBg2')
			"
		>
			<slot name="buttons"></slot>
		</div>
		<div class="tabs__container">
			<div class="tabs__header">
				<ul class="tabs__header-items" :style="tabStyle">
					<li
						:class="[
							'tabs__header-item',
							currentTabIndex === tab.index && 'is-active',
							tab.disabled && 'no-interactive'
						]"
						v-for="tab in tabs"
						:key="tab.index"
						@click="!tab.disabled ? onTabClick(tab.index) : null"
						tabindex="0"
					>
						<div
							:class="[
								'tabs__header-element',
								tab.disabled && 'is-disabled'
							]"
						>
							<div
								class="tabs__header-element-content"
								v-if="
									$slots['headerItem' + tab.index] ===
										undefined
								"
							>
								<span class="tabs__title" v-if="tab.title">
									{{ tab.title }}
								</span>
								<span
									class="tabs__sub-title"
									v-if="tab.subTitle"
								>
									{{ tab.subTitle }}
								</span>
							</div>
							<TabIconBadge
								:indicator="tab.indicator"
								:tabIcon="tab.tabIcon"
								:dataBg="tab.dataBg"
								:dataColor="tab.dataColor"
								:indicatorValue="tab.indicatorValue"
								v-if="
									(tab.indicator ||
										tab.tabIcon ||
										tab.indicatorValue) &&
										$slots['headerItem' + tab.index] ===
											undefined
								"
							/>
							<slot :name="'headerItem' + tab.index"></slot>
						</div>
					</li>
				</ul>
				<div
					class="tabs__btns"
					v-if="
						$slots.buttons &&
							!(
								variant == 'verticalBg0' ||
								variant == 'verticalBg1' ||
								variant == 'verticalBg2'
							)
					"
				>
					<slot name="buttons"></slot>
				</div>
			</div>
			<div class="tabs__tab-content" :style="tabContentStyle">
				<slot :name="currentTabIndex" />
			</div>
		</div>
	</div>
</template>

<script>
import TabIconBadge from './tab-icon-badge.vue';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/tabs-variant-classes';

export default {
	name: 'Tabs',
	components: {
		TabIconBadge
	},
	data() {
		return {
			currentTabIndex: this.selectedTabIndex
		};
	},
	props: {
		/**
		 * [
		 * 	{
		 * 		index: 1,
		 * 		title: 'Tab Title'
		 * 	}
		 * ]
		 */
		tabs: {
			type: Array,
			default: () => []
		},
		selectedTabIndex: {
			type: Number,
			default: 1
		},
		width: {
			type: [String, Number],
			default: null
		},
		indicatorValue: {
			type: [String, Number],
			default: null
		},
		contentMaxHeight: {
			type: String,
			default: 'auto'
		}
	},
	mixins: [componentVariantMixin],
	computed: {
		tabStyle() {
			if (
				typeof this.width === 'number' ||
				typeof parseInt(this.width) === 'number'
			) {
				return {
					width: `${parseInt(this.width)}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		},
		tabContentStyle() {
			let styles = { maxHeight: this.contentMaxHeight };

			return styles;
		}
	},
	created() {
		this.variantClasses = variantClasses;
	},
	methods: {
		onTabClick(tabIndex) {
			this.currentTabIndex = tabIndex;
			this.$emit('tabChange', this.currentTabIndex);
		}
	}
};
</script>

<style lang="scss" scoped>
.tabs {
	&__header-items {
		display: inline-flex;
		align-items: stretch;
		gap: $gutter-xs;
	}

	&__header {
		display: flex;
		justify-content: space-between;
		margin-bottom: -2px;
	}

	&__btns {
		align-self: flex-end;
		padding: 0 $padding-sm $padding-sm $padding-md;
	}

	&__header-item {
		display: flex;
		align-items: center;
		flex-direction: column-reverse;
		justify-content: flex-end;
		position: relative;

		&:focus,
		&:focus-visible,
		&:visited {
			outline: none;
		}

		&:not(.no-interactive) {
			cursor: pointer;
		}

		&::after {
			content: '';
			height: 6px;
			width: 100%;
			border-top-left-radius: $border-radius;
			border-top-right-radius: $border-radius;
			z-index: $z-level1-2;
			position: absolute;
			top: -3px;
		}

		&.is-active {
			&::after {
				background: var(--brand-2);
			}

			.tabs__header-element {
				border-left-color: var(--neutral-2);
				border-right-color: var(--neutral-2);
				background: var(--bg-0);
				color: var(--bg-0-fg-4);
			}
		}

		&.is-active:first-child {
			.tabs__header-element {
				border-left-color: transparent;
			}
		}

		&:not(.is-active):not(.no-interactive) {
			&:hover,
			&:focus {
				border-top-left-radius: $border-radius;
				border-top-right-radius: $border-radius;
				background: var(--brand-2-tint);

				&:deep(.color-indicator__square) {
					background: var(--ui-accessible-need);
				}
			}
		}
	}

	&__header-element {
		flex-grow: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: $gutter-md;
		border: 2px solid transparent;
		color: var(--bg-0-fg-4);
		padding: $padding-sm $padding-md;
		text-align: center;
		z-index: $z-level1-1;
	}

	&__header-element-content > * {
		display: block;
	}

	&__title {
		@include heading('sm');
	}

	&__sub-title {
		font-size: $txt-font-size;
		margin-top: $gutter-sm;
	}

	&__tab-content {
		min-height: 320px;
		padding: $padding-md $padding-sm $padding-md $padding-md; // padding on left/right matches standard padding on page content
		border-top: 2px solid var(--neutral-2);
		background: var(--bg-0);
		color: var(--bg-0-fg-4);
		position: relative;
		overflow-x: hidden;
	}

	&.tabs--vertical {
		display: flex;
		flex-direction: column;

		.tabs {
			&__container {
				display: flex;
			}

			&__header {
				margin-bottom: 0;
				margin-right: -2px;
			}

			&__header-items {
				flex-direction: column;
				align-items: stretch;
				border-color: transparent;
			}

			&__header-item {
				flex-direction: row-reverse;
				text-align: left;
				margin-bottom: 0;
				width: 180px;

				&::after {
					width: 6px;
					min-width: 5px;
					height: 100%;
					transform: translate(1px, 3px);
					border-top-right-radius: 0;
					border-bottom-left-radius: $border-radius;
				}

				&.is-active {
					.tabs__header-element {
						border-left-color: transparent;
						border-right-color: transparent;
						border-top-color: var(--neutral-2);
						border-bottom-color: var(--neutral-2);
						z-index: $z-level1-1;
					}
				}

				&:not(.is-active):not(.no-interactive) {
					&:hover,
					&:focus {
						border-top-right-radius: 0;
						border-bottom-left-radius: $border-radius;
						background: var(--brand-2-tint);
					}
				}
			}

			&__header-element {
				padding: $padding-md;
				justify-content: space-between;
				align-items: center;
				border-bottom-left-radius: $border-radius;
				border-top-left-radius: $border-radius;
			}

			&__title,
			&__sub-title {
				text-align: left;
			}

			&__tab-content {
				margin-top: 0;
				position: relative;
				flex-grow: 2;
				min-width: 320px;
				border-top: 0;
				border-left: 2px solid var(--neutral-2);
			}
		}
	}
}

/* Variant Styles */

.tabs--bg-1 {
	.tabs__tab-content {
		background: var(--bg-1);
		color: var(--bg-1-fg-3);
	}
	.tabs__header-item {
		&.is-active {
			.tabs__header-element {
				background: var(--bg-1);
				color: var(--bg-1-fg-3);
			}
		}
	}
}

.tabs--bg-2 {
	.tabs__tab-content {
		background: var(--bg-2);
		color: var(--bg-2-fg-3);
	}
	.tabs__header-item {
		&.is-active {
			.tabs__header-element {
				background: var(--bg-2);
				color: var(--bg-2-fg-3);
			}
		}
	}
}

.tabs--highlight-1 {
	.tabs__header-item {
		&.is-active {
			&::after {
				background: var(--menu-brand-1-selected-bg);
			}
		}
		&:not(.is-active):not(.no-interactive) {
			&:hover,
			&:focus {
				background: var(--nav-brand-1-tint);
			}
		}
	}
}
</style>
